import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const engagementPackages = [
  {
    title: "Launch Complete",
    serviceKey: "website_full_service",
    engagementType: "full_service",
    who: "For clients who want domain, email, SSL, and launch managed by us",
    setupFee: 75000,
    recurringFee: 24000,
    recurringCycle: "yearly",
    domainRequired: false,
    includes: [
      "Website design and development",
      "Domain, SSL, and business email coordination",
      "Launch, QA, and analytics setup",
      "Priority support during launch phase",
    ],
    renewalItems: [
      { key: "domain_renewal", label: "Domain Renewal", amount: 1200, cycle: "yearly" },
      { key: "email_renewal", label: "Business Email Renewal", amount: 4200, cycle: "yearly" },
      { key: "ssl_renewal", label: "SSL Renewal", amount: 1800, cycle: "yearly" },
      { key: "care_plan", label: "Website Care Plan", amount: 16800, cycle: "yearly" },
    ],
    milestones: [
      { key: "kickoff", label: "Kickoff Advance", percent: 30 },
      { key: "design_approval", label: "Design Approval", percent: 40 },
      { key: "go_live", label: "Go-Live Handover", percent: 30 },
    ],
  },
  {
    title: "Build & Deploy",
    serviceKey: "website_own_domain",
    engagementType: "own_domain",
    who: "For clients who already own a domain and need only website build + launch",
    setupFee: 55000,
    recurringFee: 9000,
    recurringCycle: "yearly",
    domainRequired: true,
    includes: [
      "Website design and development",
      "Deployment on production infrastructure",
      "SSL enablement and launch checklist",
      "Post-launch support handover",
    ],
    renewalItems: [
      { key: "support_retainer", label: "Support Retainer", amount: 9000, cycle: "yearly" },
    ],
    milestones: [
      { key: "kickoff", label: "Kickoff Advance", percent: 30 },
      { key: "staging_signoff", label: "Staging Sign-Off", percent: 40 },
      { key: "go_live", label: "Production Launch", percent: 30 },
    ],
  },
];

const normalizeDomain = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

const isValidDomain = (value) =>
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value);

const formatInr = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

const Hosting = () => {
  const navigate = useNavigate();
  const [domainName, setDomainName] = useState("");
  const [selectedMilestones, setSelectedMilestones] = useState({
    website_full_service: 0,
    website_own_domain: 0,
  });
  const [domainValidationLoading, setDomainValidationLoading] = useState(false);
  const [domainValidationResult, setDomainValidationResult] = useState(null);
  const [domainValidationError, setDomainValidationError] = useState("");

  const cleanedDomain = useMemo(() => normalizeDomain(domainName), [domainName]);
  const isDomainReady = isValidDomain(cleanedDomain);

  const startCheckout = (pkg) => {
    if (pkg.domainRequired && !isDomainReady) {
      return;
    }

    const milestoneIndex = selectedMilestones[pkg.serviceKey] || 0;
    const selectedMilestone = pkg.milestones[milestoneIndex] || pkg.milestones[0];
    const milestonePlan = pkg.milestones.map((milestone) => ({
      ...milestone,
      amount: Math.round((pkg.setupFee * milestone.percent) / 100),
    }));
    const milestoneAmount = milestonePlan[milestoneIndex]?.amount || milestonePlan[0].amount;

    navigate("/payment/razorpay", {
      state: {
        orderType: "website",
        serviceCategory: "website",
        serviceKey: pkg.serviceKey,
        amount: milestoneAmount,
        serviceName: pkg.title,
        engagementType: pkg.engagementType,
        domainName: pkg.domainRequired ? cleanedDomain : cleanedDomain || "",
        domainSource: pkg.engagementType === "full_service" ? "asiduo_registered" : "external",
        setupPreference: pkg.title,
        feeBreakdown: {
          setupFee: pkg.setupFee,
          recurringFee: pkg.recurringFee,
          recurringCycle: pkg.recurringCycle,
          totalProjectValue: pkg.setupFee + pkg.recurringFee,
          currentMilestoneKey: selectedMilestone.key,
          currentMilestoneLabel: selectedMilestone.label,
          currentMilestoneAmount: milestoneAmount,
          milestonesCount: pkg.milestones.length,
          renewalItems: pkg.renewalItems,
        },
        milestonePlan,
        notes: `Customer selected ${pkg.title}. Current payment milestone: ${selectedMilestone.label}.`,
      },
    });
  };

  const validateWebsiteDomain = async () => {
    if (!isDomainReady) {
      setDomainValidationError("Enter a valid root domain like example.com.");
      setDomainValidationResult(null);
      return null;
    }

    setDomainValidationLoading(true);
    setDomainValidationError("");

    try {
      const response = await fetch(
        buildApiUrl(`/api/website-domain-validate?domain=${encodeURIComponent(cleanedDomain)}`)
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Domain validation failed.");
      }

      setDomainValidationResult(data);
      return data;
    } catch (error) {
      setDomainValidationResult(null);
      setDomainValidationError(error.message);
      return null;
    } finally {
      setDomainValidationLoading(false);
    }
  };

  const handleDomainSubmit = async (event) => {
    event.preventDefault();

    const validation = await validateWebsiteDomain();

    if (!validation) {
      return;
    }

    if (!validation.exists) {
      return;
    }

    const packageToRoute = engagementPackages.find(
      (pkg) => pkg.serviceKey === validation.recommendedPackageKey
    );

    if (!packageToRoute) {
      return;
    }

    startCheckout(packageToRoute);
  };

  const handlePackageCheckout = async () => {
    const validation = await validateWebsiteDomain();

    if (!validation || !validation.exists) {
      return;
    }

    const packageToRoute = engagementPackages.find(
      (pkg) => pkg.serviceKey === validation.recommendedPackageKey
    );

    if (!packageToRoute) {
      return;
    }

    startCheckout(packageToRoute);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-[#15122f] to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl"></div>
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl"></div>

      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="rounded-[36px] border border-white/10 bg-white/5 p-8 backdrop-blur-xl md:p-12"
        >
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-cyan-300">Website Engagement</p>
          <h1 className="mt-4 text-4xl font-black leading-tight md:text-6xl">
            We Build And Launch
            <span className="bg-gradient-to-r from-cyan-300 to-fuchsia-400 bg-clip-text text-transparent">
              {" "}Your Website
            </span>
          </h1>
          <p className="mt-6 max-w-4xl text-lg text-slate-300">
            Pick your engagement type, pay milestone-wise, and track project delivery. We keep the
            technical deployment workflow internal so your pricing and deliverables stay simple.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              "Milestone-based billing",
              "Outcome-focused packages",
              "No technical hosting complexity in customer journey",
            ].map((item) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                {item}
              </div>
            ))}
          </div>
        </motion.div>

        <form
          onSubmit={handleDomainSubmit}
          className="mt-14 rounded-[32px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl md:p-8"
        >
          <h2 className="text-2xl font-black md:text-3xl">Website Domain</h2>
          <p className="mt-2 text-slate-300">
          Enter your existing domain name if available. Don't have one yet? We can register and configure a domain for you.
          </p>
          <input
            type="text"
            value={domainName}
            onChange={(event) => {
              setDomainName(event.target.value);
              setDomainValidationResult(null);
              setDomainValidationError("");
            }}
            placeholder="example.com"
            className="mt-5 h-14 w-full rounded-2xl border border-white/10 bg-white/10 px-5 text-white outline-none placeholder:text-slate-400 focus:border-fuchsia-400"
          />
          {domainName && !isDomainReady ? (
            <p className="mt-3 text-sm font-semibold text-rose-300">Enter a valid root domain like example.com.</p>
          ) : null}
          {domainValidationError ? (
            <p className="mt-3 text-sm font-semibold text-rose-300">{domainValidationError}</p>
          ) : null}
          {domainValidationResult && !domainValidationResult.exists ? (
            <p className="mt-3 text-sm font-semibold text-amber-300">
              Domain does not exist. Purchase the domain{" "}
              <button
                type="button"
                onClick={() => navigate("/domain")}
                className="underline underline-offset-4"
              >
                click here
              </button>
              .
            </p>
          ) : null}
          {domainValidationResult?.exists && domainValidationResult.source === "asiduo_registered" ? (
            <p className="mt-3 text-sm font-semibold text-emerald-300">
              Domain is purchased from Asiduo. Redirecting to full package payment.
            </p>
          ) : null}
          {domainValidationResult?.exists && domainValidationResult.source === "external" ? (
            <p className="mt-3 text-sm font-semibold text-emerald-300">
              Domain exists externally. Redirecting to website launch package payment.
            </p>
          ) : null}
          {isDomainReady ? (
            <p className="mt-3 text-sm font-semibold text-emerald-300">
              {domainValidationLoading
                ? "Checking domain availability and source..."
                : "Press Enter to validate domain and continue to the correct package."}
            </p>
          ) : null}
        </form>

        <section className="mt-14 grid gap-8 md:grid-cols-2">
          {engagementPackages.map((pkg) => {
            const milestoneIndex = selectedMilestones[pkg.serviceKey] || 0;
            const selectedMilestone = pkg.milestones[milestoneIndex] || pkg.milestones[0];
            const payableAmount = Math.round((pkg.setupFee * selectedMilestone.percent) / 100);
            const canProceed = isDomainReady && !domainValidationLoading;

            return (
              <motion.article
                key={pkg.serviceKey}
                whileHover={{ y: -6 }}
                className="rounded-[30px] border border-white/10 bg-white/5 p-7 backdrop-blur-xl"
              >
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-fuchsia-300">{pkg.engagementType === "full_service" ? "Full package" : "Website Launch Package"}</p>
                <h3 className="mt-3 text-3xl font-black">{pkg.title}</h3>
                <p className="mt-3 text-sm text-slate-300">{pkg.who}</p>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-cyan-300/20 bg-cyan-500/10 p-4">
                    <p className="text-xs text-slate-300">Setup Fee</p>
                    <p className="mt-1 text-xl font-black text-cyan-200">{formatInr(pkg.setupFee)}</p>
                  </div>
                  <div className="rounded-2xl border border-fuchsia-300/20 bg-fuchsia-500/10 p-4">
                    <p className="text-xs text-slate-300">Recurring</p>
                    <p className="mt-1 text-xl font-black text-fuchsia-200">{formatInr(pkg.recurringFee)} / {pkg.recurringCycle}</p>
                  </div>
                </div>

                <div className="mt-6 space-y-2 text-sm text-slate-200">
                  {pkg.includes.map((item) => (
                    <p key={item}>• {item}</p>
                  ))}
                </div>

                <div className="mt-7">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Select Milestone To Pay</p>
                  <div className="mt-3 grid gap-2">
                    {pkg.milestones.map((milestone, index) => {
                      const amount = Math.round((pkg.setupFee * milestone.percent) / 100);
                      const selected = milestoneIndex === index;

                      return (
                        <button
                          key={milestone.key}
                          type="button"
                          onClick={() =>
                            setSelectedMilestones((current) => ({
                              ...current,
                              [pkg.serviceKey]: index,
                            }))
                          }
                          className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                            selected
                              ? "border-emerald-300/40 bg-emerald-500/15 text-emerald-100"
                              : "border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                          }`}
                        >
                          <span className="block font-semibold">{milestone.label}</span>
                          <span className="text-xs text-slate-300">{milestone.percent}% • {formatInr(amount)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-slate-950/50 p-4">
                  <p className="text-xs text-slate-400">Current Payable</p>
                  <p className="mt-1 text-2xl font-black text-emerald-300">{formatInr(payableAmount)}</p>
                  <p className="mt-1 text-xs text-slate-400">Milestone: {selectedMilestone.label}</p>
                </div>

                <button
                  type="button"
                  onClick={handlePackageCheckout}
                  disabled={!canProceed}
                  className="mt-7 w-full rounded-2xl bg-gradient-to-r from-cyan-600 to-fuchsia-600 py-4 font-bold transition-all duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {domainValidationLoading
                    ? "Validating Domain..."
                    : canProceed
                      ? "Continue To Payment"
                      : "Enter Valid Domain First"}
                </button>
              </motion.article>
            )
          })}
        </section>
      </div>
    </div>
  );
};

export default Hosting;
